import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import Header from '../components/Header';
import { Camera, Upload, ArrowLeft, Check } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ImageCropModal from '../components/ImageCropModal';
import GooglePlacesInput from '../components/GooglePlacesInput';
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TEST_BLOCKS = [
  { id: 'gastos', title: 'Gastos comunes', emoji: '🌡️', options: [ { value: 1, label: 'En invierno me gusta tener la calefacción alta todo el día.' }, { value: 2, label: 'En invierno suelo poner la calefacción por la noche y usar ropa de abrigo.' }, { value: 3, label: 'Vivo con la calefacción ajustada.' } ] },
  { id: 'social', title: 'Vida social y visitas', emoji: '🎉', options: [ { value: 1, label: 'No me gusta estar mucho tiempo en soledad; suelo recibir personas en casa.' }, { value: 2, label: 'Podría convivir con personas amigas o conocidas un par de fines de semana al mes.' }, { value: 3, label: 'Es poco habitual que reciba muchas visitas en casa.' } ] },
  { id: 'espacios', title: 'Uso de espacios comunes', emoji: '🏠', options: [ { value: 1, label: 'Suelo hacer vida en los espacios comunes y me gusta compartirlos.' }, { value: 2, label: 'Me adapto al uso de los espacios comunes.' }, { value: 3, label: 'Me gusta mantener mis cosas organizadas.' } ] },
  { id: 'animo', title: 'Ánimo y estilo de vida', emoji: '✨', options: [ { value: 1, label: 'Por lo general tengo energía y soy una persona activa en casa.' }, { value: 2, label: 'Mi estado de ánimo puede variar.' }, { value: 3, label: 'Prefiero un ambiente tranquilo.' } ] },
  { id: 'tareas', title: 'Tareas del hogar', emoji: '🧹', options: [ { value: 1, label: 'Suelo repartir y cumplir con las tareas del hogar.' }, { value: 2, label: 'Cada persona se encarga de lo que ensucia en el momento.' }, { value: 3, label: 'Me interesa contratar ayuda para las tareas del hogar.' } ] },
];

function calcRoommateProfile(answers) {
  const total = Object.values(answers).reduce((s, v) => s + v, 0);
  if (total <= 8) return 'Abierto';
  if (total <= 12) return 'Equilibrado';
  return 'Reservado';
}

const ROOMMATE_PROFILE_STYLES = {
  Abierto:     'bg-pink-100 text-pink-700 border border-pink-200',
  Equilibrado: 'bg-orange-100 text-orange-700 border border-orange-200',
  Reservado:   'bg-violet-100 text-violet-700 border border-violet-200',
};

const ALL_ACTIVITIES = [
  'Deportes', 'Cine', 'Videojuegos', 'Lectura', 'Fiesta', 'Instrumentos musicales', 'Vino y charla', '4 20', 'Cantar', 'Cocinar',
  'Redes sociales', 'Senderismo', 'Gimnasio', 'Running', 'Viajar', 'Yoga', 'Bailar', 'Moda', 'Animales', 'Jardinería',
  'Tecnología', 'Motor', 'Pintura', 'Tapear', 'Festivales y conciertos', 'Compras',
];

const NATIONALITY_OPTIONS = [
  { value: 'Española', label: '🇪🇸 España' }, { value: 'Alemana', label: '🇩🇪 Alemania' }, { value: 'Francesa', label: '🇫🇷 Francia' }, { value: 'Italiana', label: '🇮🇹 Italia' }, { value: 'Portuguesa', label: '🇵🇹 Portugal' }, { value: 'Británica', label: '🇬🇧 Reino Unido' }, { value: 'Holandesa', label: '🇳🇱 Holanda' }, { value: 'Belga', label: '🇧🇪 Bélgica' }, { value: 'Sueca', label: '🇸🇪 Suecia' }, { value: 'Noruega', label: '🇳🇴 Noruega' }, { value: 'Danesa', label: '🇩🇰 Dinamarca' }, { value: 'Finlandesa', label: '🇫🇮 Finlandia' }, { value: 'Polaca', label: '🇵🇱 Polonia' }, { value: 'Rumana', label: '🇷🇴 Rumanía' }, { value: 'Griega', label: '🇬🇷 Grecia' }, { value: 'Checa', label: '🇨🇿 República Checa' }, { value: 'Húngara', label: '🇭🇺 Hungría' }, { value: 'Austriaca', label: '🇦🇹 Austria' }, { value: 'Suiza', label: '🇨🇭 Suiza' }, { value: 'Americana', label: '🇺🇸 Estados Unidos' }, { value: 'Mexicana', label: '🇲🇽 México' }, { value: 'Argentina', label: '🇦🇷 Argentina' }, { value: 'Colombiana', label: '🇨🇴 Colombia' }, { value: 'Venezolana', label: '🇻🇪 Venezuela' }, { value: 'Peruana', label: '🇵🇪 Perú' }, { value: 'Chilena', label: '🇨🇱 Chile' }, { value: 'Brasileña', label: '🇧🇷 Brasil' }, { value: 'Ecuatoriana', label: '🇪🇨 Ecuador' }, { value: 'Boliviana', label: '🇧🇴 Bolivia' }, { value: 'Uruguaya', label: '🇺🇾 Uruguay' }, { value: 'Paraguaya', label: '🇵🇾 Paraguay' }, { value: 'China', label: '🇨🇳 China' }, { value: 'Japonesa', label: '🇯🇵 Japón' }, { value: 'Coreana', label: '🇰🇷 Corea del Sur' }, { value: 'India', label: '🇮🇳 India' }, { value: 'Pakistaní', label: '🇵🇰 Pakistán' }, { value: 'Marroquí', label: '🇲🇦 Marruecos' }, { value: 'Argelina', label: '🇩🇿 Argelia' }, { value: 'Senegalesa', label: '🇸🇳 Senegal' }, { value: 'Nigeriana', label: '🇳🇬 Nigeria' }, { value: 'Otra', label: '🌍 Otra' },
];

function emptyAnswers() { return { gastos: null, social: null, espacios: null, animo: null, tareas: null }; }

function NativeSelect({ value, onChange, placeholder, options, testId }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-10 px-3 py-2 rounded-xl bg-gray-50 border border-gray-200 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[hsl(144,18%,80%)] focus:border-[hsl(144,18%,45%)]" data-testid={testId}>
      <option value="">{placeholder}</option>
      {options.map((opt) => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
    </select>
  );
}

function SimpleToggle({ checked, onChange, testId }) {
  return (
    <button type="button" role="switch" aria-checked={checked} onClick={() => onChange(!checked)} className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-[hsl(144,18%,45%)]' : 'bg-gray-200'}`} data-testid={testId}>
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'}`} />
    </button>
  );
}

export default function EditProfile() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  const coverInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [coverFile, setCoverFile] = useState(null);
  const [coverPreview, setCoverPreview] = useState(null);
  const [cropModalSrc, setCropModalSrc] = useState(null);
  const [cropModalType, setCropModalType] = useState(null);
  const [testAnswers, setTestAnswers] = useState(emptyAnswers());
  const [selectedActivities, setSelectedActivities] = useState(user?.activity_tags || []);

  const [formData, setFormData] = useState({
    name: user?.name || '', age: user?.age?.toString() || '', city: user?.city || '',
    neighborhood: user?.neighborhood || '', budget: user?.budget?.toString() || '',
    move_in_date: user?.move_in_date || '', stay_duration: user?.stay_duration || '',
    occupation: user?.occupation || '', bio: user?.bio || '', preferences: user?.preferences || '',
    cleaning_habits: user?.cleaning_habits || '', sociability: user?.sociability || '',
    noise_habits: user?.noise_habits || '', is_smoker: user?.is_smoker || false,
    accepts_smokers: user?.accepts_smokers !== false, has_pets: user?.has_pets || false,
    accepts_pets: user?.accepts_pets !== false, schedule: user?.schedule || '',
    contact_email: user?.contact_email || user?.email || '', contact_link: user?.contact_link || '',
    allow_direct_contact: user?.allow_direct_contact || false, gender: user?.gender || '',
    preferred_flatmate_gender: user?.preferred_flatmate_gender || '', num_flatmates: user?.num_flatmates || '',
    nationality: user?.nationality || '', lgbtq_friendly: user?.lgbtq_friendly || false,
    erasmus: user?.erasmus || false,
    offers_room: user?.offers_room || '',
  });

  const updateField = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const toggleActivity = (activity) => {
    setSelectedActivities(prev => {
      if (prev.includes(activity)) return prev.filter(a => a !== activity);
      if (prev.length >= 6) { toast.error('Selecciona máximo 6 actividades'); return prev; }
      return [...prev, activity];
    });
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('La imagen es demasiado grande (máx 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => { setCropModalSrc(reader.result); setCropModalType('photo'); };
      reader.readAsDataURL(file);
    }
  };

  const uploadPhoto = async () => {
    if (!photoFile) return null;
    const formDataFile = new FormData();
    formDataFile.append('file', photoFile);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/profile/photo`, formDataFile, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data', ...(token && { Authorization: `Bearer ${token}` }) } });
      return response.data.path;
    } catch { toast.error('Error al subir la foto'); return null; }
  };

  const handleCoverChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) { toast.error('La imagen es demasiado grande (máx 5MB)'); return; }
      const reader = new FileReader();
      reader.onloadend = () => { setCropModalSrc(reader.result); setCropModalType('cover'); };
      reader.readAsDataURL(file);
    }
  };

  const handleCropComplete = (blob) => {
    const file = new File([blob], 'image.jpg', { type: 'image/jpeg' });
    if (cropModalType === 'photo') { setPhotoFile(file); setPhotoPreview(URL.createObjectURL(blob)); }
    else { setCoverFile(file); setCoverPreview(URL.createObjectURL(blob)); }
    setCropModalSrc(null); setCropModalType(null);
  };

  const uploadCover = async () => {
    if (!coverFile) return null;
    const formDataFile = new FormData();
    formDataFile.append('file', coverFile);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`${API}/profile/cover-photo`, formDataFile, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data', ...(token && { Authorization: `Bearer ${token}` }) } });
      return response.data.cover_photo;
    } catch { toast.error('Error al subir la foto de portada'); return null; }
  };

  const getPhotoUrl = (photo) => {
    if (!photo) return 'https://images.unsplash.com/photo-1760551937527-2bc6cfe45180?w=400';
    if (photo.startsWith('http')) return photo;
    return `${API}/files/${photo}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let photoPath = null;
      if (photoFile) photoPath = await uploadPhoto();
      let coverPath = null;
      if (coverFile) coverPath = await uploadCover();
      const allAnswered = TEST_BLOCKS.every(b => testAnswers[b.id] !== null);
      const roommateProfile = allAnswered ? calcRoommateProfile(testAnswers) : (user?.roommate_profile || null);
      const profileData = {
        ...formData,
        age: formData.age ? parseInt(formData.age) : null,
        budget: formData.budget ? parseInt(formData.budget) : null,
        profile_photo: photoPath || user?.profile_photo || null,
        cover_photo: coverPath || user?.cover_photo || null,
        gender: formData.gender || null,
        preferred_flatmate_gender: formData.preferred_flatmate_gender || null,
        num_flatmates: formData.num_flatmates || null,
        nationality: formData.nationality || null,
        offers_room: formData.offers_room || null,
        roommate_profile: roommateProfile,
        activity_tags: selectedActivities,
      };
      const token = localStorage.getItem('access_token');
      await axios.put(`${API}/profile`, profileData, { withCredentials: true, headers: { ...(token && { Authorization: `Bearer ${token}` }) } });
      await refreshUser();
      toast.success('Perfil actualizado correctamente');
      navigate('/profile');
    } catch { toast.error('Error al guardar el perfil'); }
    finally { setLoading(false); }
  };

  const stayDurationOptions = [ { value: '1-3 meses', label: '1-3 meses' }, { value: '3-6 meses', label: '3-6 meses' }, { value: '6-12 meses', label: '6-12 meses' }, { value: 'Más de 1 año', label: 'Más de 1 año' }, { value: 'Indefinido', label: 'Indefinido' } ];
  const cleaningOptions = [ { value: 'Muy ordenado', label: 'Muy ordenado' }, { value: 'Ordenado', label: 'Ordenado' }, { value: 'Normal', label: 'Normal' }, { value: 'Relajado', label: 'Relajado' } ];
  const sociabilityOptions = [ { value: 'Muy sociable', label: 'Muy sociable' }, { value: 'Sociable', label: 'Sociable' }, { value: 'Equilibrado', label: 'Equilibrado' }, { value: 'Reservado', label: 'Reservado' }, { value: 'Muy reservado', label: 'Muy reservado' } ];
  const noiseOptions = [ { value: 'Muy silencioso', label: 'Muy silencioso' }, { value: 'Silencioso', label: 'Silencioso' }, { value: 'Normal', label: 'Normal' }, { value: 'A veces ruidoso', label: 'A veces ruidoso' } ];
  const scheduleOptions = [ { value: 'Madrugador', label: 'Madrugador' }, { value: 'Horario normal', label: 'Horario normal' }, { value: 'Noctámbulo', label: 'Noctámbulo' }, { value: 'Horario variable', label: 'Horario variable' } ];
  const genderOptions = [ { value: 'male', label: 'Hombre' }, { value: 'female', label: 'Mujer' }, { value: 'prefer_not_to_say', label: 'Prefiero no decirlo' } ];
  const preferredFlatmateGenderOptions = [ { value: 'male', label: 'Hombres' }, { value: 'female', label: 'Mujeres' }, { value: 'any', label: 'Indiferente' } ];
  const numFlatmatesOptions = [ { value: '1', label: '1 persona' }, { value: '2', label: '2 personas' }, { value: '3_or_more', label: '3 o más personas' }, { value: 'any', label: 'Indiferente' } ];

  const allAnswered = TEST_BLOCKS.every(b => testAnswers[b.id] !== null);
  const previewProfile = allAnswered ? calcRoommateProfile(testAnswers) : null;

  return (
    <div className="min-h-screen bg-[hsl(35,25%,91%)]">
      <Header />
      <main className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button onClick={() => navigate('/profile')} className="flex items-center gap-2 text-[hsl(20,10%,40%)] hover:text-[hsl(20,10%,25%)] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" />Volver al perfil
        </button>

        <div className="bg-white rounded-2xl shadow-sm border border-[hsl(35,20%,88%)] p-6 sm:p-8">
          <h1 className="font-heading font-bold text-2xl text-gray-900 mb-6">Editar perfil</h1>
          <form onSubmit={handleSubmit} className="space-y-8">

            {/* Photo */}
            <div className="flex flex-col items-center">
              <div className="w-32 h-32 rounded-2xl bg-[hsl(35,25%,94%)] border-2 border-dashed border-[hsl(35,20%,75%)] flex items-center justify-center cursor-pointer hover:border-[hsl(35,20%,60%)] transition-colors overflow-hidden" onClick={() => fileInputRef.current?.click()} data-testid="photo-upload-area">
                {photoPreview || user?.profile_photo ? <img src={photoPreview || getPhotoUrl(user?.profile_photo)} alt="Preview" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-[hsl(35,20%,60%)]" />}
              </div>
              <input ref={fileInputRef} type="file" accept="image/*" onChange={handlePhotoChange} className="hidden" />
              <button type="button" onClick={() => fileInputRef.current?.click()} className="mt-2 text-sm text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)] flex items-center gap-1">
                <Upload className="w-4 h-4" />Cambiar foto
              </button>
            </div>

            {/* Cover Photo */}
            <div className="space-y-2">
              <h2 className="font-heading font-semibold text-lg text-gray-900">Foto de portada</h2>
              <div className="w-full h-32 rounded-xl bg-[hsl(35,25%,94%)] border-2 border-dashed border-[hsl(35,20%,75%)] flex items-center justify-center cursor-pointer hover:border-[hsl(35,20%,60%)] overflow-hidden" onClick={() => coverInputRef.current?.click()}>
                {coverPreview || user?.cover_photo ? <img src={coverPreview || user?.cover_photo} alt="Portada" className="w-full h-full object-cover" /> : <Camera className="w-8 h-8 text-[hsl(35,20%,60%)]" />}
              </div>
              <input ref={coverInputRef} type="file" accept="image/*" onChange={handleCoverChange} className="hidden" />
              <button type="button" onClick={() => coverInputRef.current?.click()} className="mt-2 text-sm text-[hsl(144,18%,45%)] hover:text-[hsl(144,18%,38%)] flex items-center gap-1">
                <Upload className="w-4 h-4" />Cambiar portada
              </button>
            </div>

            {/* Basic Info */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg text-gray-900">Información básica</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label htmlFor="name">Nombre</Label><Input id="name" value={formData.name} onChange={(e) => updateField('name', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-name-input" /></div>
                <div><Label htmlFor="age">Edad</Label><Input id="age" type="number" value={formData.age} onChange={(e) => updateField('age', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-age-input" /></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Género</Label><div className="mt-1"><NativeSelect value={formData.gender} onChange={(v) => updateField('gender', v)} placeholder="Selecciona tu género" options={genderOptions} testId="edit-gender-select" /></div></div>
                <div><Label>Nacionalidad</Label><div className="mt-1"><NativeSelect value={formData.nationality} onChange={(v) => updateField('nationality', v)} placeholder="Selecciona" options={NATIONALITY_OPTIONS} testId="edit-nationality-select" /></div></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Ciudad</Label><GooglePlacesInput value={formData.city} onChange={(value) => updateField('city', value)} placeholder="Ej: Madrid, Barcelona..." className="mt-1 rounded-xl bg-gray-50 w-full h-10 px-3 py-2 border border-gray-200 text-gray-900" /></div>
                <div><Label>Zona / Barrio</Label><GooglePlacesInput type="neighborhood" value={formData.neighborhood} onChange={(value) => updateField('neighborhood', value)} placeholder="Ej: Malasaña, Gràcia..." className="mt-1 rounded-xl bg-gray-50 w-full h-10 px-3 py-2 border border-gray-200 text-gray-900" /></div>
              </div>
              <div><Label htmlFor="occupation">Ocupación</Label><Input id="occupation" value={formData.occupation} onChange={(e) => updateField('occupation', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-occupation-input" /></div>
              <div><Label htmlFor="bio">Biografía</Label><Textarea id="bio" value={formData.bio} onChange={(e) => updateField('bio', e.target.value)} className="mt-1 rounded-xl bg-gray-50 min-h-[100px]" data-testid="edit-bio-input" /></div>
              <div className="space-y-3">
                <div className="flex items-center justify-between p-4 bg-[hsl(35,25%,94%)] rounded-xl">
                  <div><p className="font-medium text-gray-900">🏳️‍🌈 LGBTQ+ Friendly</p><p className="text-sm text-gray-500">Ambiente inclusivo y respetuoso</p></div>
                  <SimpleToggle checked={formData.lgbtq_friendly} onChange={(v) => updateField('lgbtq_friendly', v)} testId="edit-lgbtq-switch" />
                </div>
                <div className="flex items-center justify-between p-4 bg-[hsl(35,25%,94%)] rounded-xl">
                  <div><p className="font-medium text-gray-900">✈️ Erasmus</p><p className="text-sm text-gray-500">Estoy en programa Erasmus o similar</p></div>
                  <SimpleToggle checked={formData.erasmus} onChange={(v) => updateField('erasmus', v)} testId="edit-erasmus-switch" />
                </div>
              </div>
            </div>

            {/* Search Details */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg text-gray-900">Detalles de búsqueda</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label htmlFor="budget">Presupuesto (€/mes)</Label><Input id="budget" type="number" value={formData.budget} onChange={(e) => updateField('budget', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-budget-input" /></div>
                <div><Label htmlFor="move_in_date">Fecha de entrada</Label><Input id="move_in_date" type="date" value={formData.move_in_date} onChange={(e) => updateField('move_in_date', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-move-in-date-input" /></div>
              </div>
              <div><Label>Duración prevista</Label><div className="mt-1"><NativeSelect value={formData.stay_duration} onChange={(v) => updateField('stay_duration', v)} placeholder="Selecciona" options={stayDurationOptions} testId="edit-stay-duration-select" /></div></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Quiero convivir con</Label><div className="mt-1"><NativeSelect value={formData.preferred_flatmate_gender} onChange={(v) => updateField('preferred_flatmate_gender', v)} placeholder="Selecciona" options={preferredFlatmateGenderOptions} testId="edit-preferred-flatmate-gender-select" /></div></div>
                <div><Label>Número de compañeros</Label><div className="mt-1"><NativeSelect value={formData.num_flatmates} onChange={(v) => updateField('num_flatmates', v)} placeholder="Selecciona" options={numFlatmatesOptions} testId="edit-num-flatmates-select" /></div></div>
              </div>
              <div><Label htmlFor="preferences">Preferencias de convivencia</Label><Textarea id="preferences" value={formData.preferences} onChange={(e) => updateField('preferences', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-preferences-input" /></div>
              <div>
                <Label>🏠 Ofrezco habitación</Label>
                <div className="mt-1">
                  <NativeSelect
                    value={formData.offers_room}
                    onChange={(v) => updateField('offers_room', v)}
                    placeholder="No ofrezco habitación"
                    options={[
                      { value: '1', label: '1 habitación' },
                      { value: '2', label: '2 habitaciones' },
                      { value: 'more_than_2', label: 'Más de 2 habitaciones' },
                    ]}
                    testId="edit-offers-room-select"
                  />
                </div>
              </div>
            </div>

            {/* Lifestyle */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg text-gray-900">Estilo de vida</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Hábitos de limpieza</Label><div className="mt-1"><NativeSelect value={formData.cleaning_habits} onChange={(v) => updateField('cleaning_habits', v)} placeholder="Selecciona" options={cleaningOptions} /></div></div>
                <div><Label>Sociabilidad</Label><div className="mt-1"><NativeSelect value={formData.sociability} onChange={(v) => updateField('sociability', v)} placeholder="Selecciona" options={sociabilityOptions} /></div></div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div><Label>Hábitos de ruido</Label><div className="mt-1"><NativeSelect value={formData.noise_habits} onChange={(v) => updateField('noise_habits', v)} placeholder="Selecciona" options={noiseOptions} /></div></div>
                <div><Label>Horario habitual</Label><div className="mt-1"><NativeSelect value={formData.schedule} onChange={(v) => updateField('schedule', v)} placeholder="Selecciona" options={scheduleOptions} /></div></div>
              </div>
              <div className="space-y-3 pt-2">
                {[ { label: '¿Fumas?', field: 'is_smoker' }, { label: '¿Aceptas fumadores?', field: 'accepts_smokers' }, { label: '¿Tienes mascotas?', field: 'has_pets' }, { label: '¿Aceptas mascotas?', field: 'accepts_pets' } ].map(({ label, field }) => (
                  <div key={field} className="flex items-center justify-between p-4 bg-[hsl(35,25%,94%)] rounded-xl">
                    <span className="text-gray-700">{label}</span>
                    <SimpleToggle checked={formData[field]} onChange={(v) => updateField(field, v)} />
                  </div>
                ))}
              </div>
            </div>

            {/* Test de convivencia */}
            <div className="space-y-4">
              <div>
                <h2 className="font-heading font-semibold text-lg text-gray-900">Test de convivencia</h2>
                {user?.roommate_profile && (
                  <p className="text-sm text-gray-500 mt-1">
                    Tu perfil actual: <span className={`ml-2 inline-block px-2 py-0.5 rounded-full text-xs font-semibold border ${ROOMMATE_PROFILE_STYLES[user.roommate_profile]}`}>{user.roommate_profile}</span>
                    <span className="ml-2 text-xs text-gray-400">— responde de nuevo para actualizarlo</span>
                  </p>
                )}
              </div>
              {TEST_BLOCKS.map((block) => (
                <div key={block.id} className="space-y-2">
                  <p className="font-medium text-gray-800 text-sm">{block.emoji} {block.title}</p>
                  <div className="space-y-2">
                    {block.options.map((opt) => {
                      const selected = testAnswers[block.id] === opt.value;
                      return (
                        <button key={opt.value} type="button" onClick={() => setTestAnswers(prev => ({ ...prev, [block.id]: opt.value }))}
                          className={`w-full text-left px-4 py-3 rounded-xl border text-sm transition-all ${selected ? 'bg-[hsl(144,18%,40%)] border-[hsl(144,18%,40%)] text-white font-medium shadow-sm' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[hsl(144,18%,60%)] hover:bg-[hsl(144,18%,95%)]'}`}>
                          <span className="flex items-center gap-2">{selected && <Check className="w-4 h-4 flex-shrink-0" />}{opt.label}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
              {previewProfile && (
                <div className="p-3 rounded-xl border-2 border-dashed border-[hsl(35,20%,75%)] bg-[hsl(35,25%,94%)] text-center">
                  <p className="text-xs text-gray-500 mb-1">Nuevo perfil de convivencia</p>
                  <span className={`inline-block px-4 py-1 rounded-full text-sm font-semibold border ${ROOMMATE_PROFILE_STYLES[previewProfile]}`}>{previewProfile}</span>
                </div>
              )}
            </div>

            {/* Gustos */}
            <div className="space-y-3">
              <div>
                <h2 className="font-heading font-semibold text-lg text-gray-900">Tus gustos</h2>
                <p className="text-sm text-gray-500 mt-1">Elige hasta <strong>6 actividades</strong> <span className="ml-1 text-[hsl(144,18%,45%)] font-medium">({selectedActivities.length}/6)</span></p>
              </div>
              <div className="flex flex-wrap gap-2">
                {ALL_ACTIVITIES.map((activity) => {
                  const selected = selectedActivities.includes(activity);
                  return (
                    <button key={activity} type="button" onClick={() => toggleActivity(activity)}
                      className={`px-3 py-1.5 rounded-full text-sm font-medium border transition-all ${selected ? 'bg-[hsl(144,18%,45%)] border-[hsl(144,18%,45%)] text-white' : 'bg-gray-50 border-gray-200 text-gray-700 hover:border-[hsl(144,18%,60%)] hover:bg-[hsl(144,18%,95%)]'}`}>
                      {selected && <Check className="w-3 h-3 inline mr-1" />}{activity}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Contact */}
            <div className="space-y-4">
              <h2 className="font-heading font-semibold text-lg text-gray-900">Contacto</h2>
              <div><Label htmlFor="contact_email">Email de contacto</Label><Input id="contact_email" type="email" value={formData.contact_email} onChange={(e) => updateField('contact_email', e.target.value)} className="mt-1 rounded-xl bg-gray-50" data-testid="edit-contact-email-input" /></div>
              <div><Label htmlFor="contact_link">Enlace externo</Label><Input id="contact_link" value={formData.contact_link} onChange={(e) => updateField('contact_link', e.target.value)} placeholder="Instagram, LinkedIn, etc." className="mt-1 rounded-xl bg-gray-50" data-testid="edit-contact-link-input" /></div>
              <div className="flex items-center justify-between p-4 bg-[hsl(35,25%,94%)] rounded-xl">
                <div><p className="font-medium text-gray-900">Permitir contacto directo</p><p className="text-sm text-gray-600">Cualquiera podrá ver tu contacto</p></div>
                <SimpleToggle checked={formData.allow_direct_contact} onChange={(v) => updateField('allow_direct_contact', v)} testId="edit-allow-direct-contact-switch" />
              </div>
            </div>

            {/* Submit */}
            <div className="flex gap-4 pt-4">
              <Button type="button" variant="outline" onClick={() => navigate('/profile')} className="flex-1 rounded-xl border-[hsl(35,20%,75%)]">Cancelar</Button>
              <Button type="submit" className="flex-1 bg-[hsl(144,18%,45%)] hover:bg-[hsl(144,18%,38%)] text-white rounded-xl" disabled={loading} data-testid="save-profile-btn">
                {loading ? 'Guardando...' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </div>
      </main>

      {cropModalSrc && (
        <ImageCropModal
          imageSrc={cropModalSrc}
          title={cropModalType === 'photo' ? 'Ajustar foto de perfil' : 'Ajustar foto de portada'}
          onCropComplete={handleCropComplete}
          onCancel={() => { setCropModalSrc(null); setCropModalType(null); }}
        />
      )}
    </div>
  );
}
